import { Component, OnInit, AfterViewInit, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventService } from 'src/app/core/service/event.service';
import { PagosService } from './pagos.service';
import { PartesService } from '../partes/partes.service';
import { SortEvent } from 'src/app/shared/advanced-table/sortable.directive';
import { Column } from 'src/app/shared/advanced-table/advanced-table.component';
import { Pago } from './pagos.model';
import { Parte } from '../partes/partes.model';
import { Router } from '@angular/router';
import { pagoTranslations } from './translations-pagos';
import { getCurrentLanguage } from '../core/helpers/utils';
import { formatDateString } from '../core/helpers/utils';
import { CasosService } from '../casos/casos.service';
import { AuthenticationService } from '../core/service/auth.service';

// Define el tipo ModelKeys fuera de la clase
type ModelKeys = keyof Pago;

@Component({
  selector: 'app-registro-pagos',
  templateUrl: './registro-pagos.component.html',
  styleUrls: ['./registro-pagos.component.scss']
})
export class RegistroPagosComponent implements OnInit {

  records: Pago[] = [];
  columns: Column[] = [];
  pageSizeOptions: number[] = [10, 25, 50, 100];
  partes: { value: string, label: string }[] = [];
  conceptos: string[] = ['DERECHO_ADMISION', 'HONORARIO_DERECHO_GASTO_EXPEDIENTE', 'GASTO_ARBITRO', 'ALQUILER_SALA']; // Conceptos posibles
  buttonTitle: string = '';
  buttonLink: string = '';
  loaded: boolean = false;
  error: string = '';
  allDatasearch: Pago[] = [];
  id_caso!: string | null;
  estadoExpediente: string | undefined;
  disableOptions: boolean = false;
  disableAnadirPago: boolean = true;


  nuevoPago: Pago = {
    id_expediente: '',
    concepto: '',
    importe: '',
    tipo_reclamacion: '',
    id_parte: '',
    fecha: ''
  };

  showConfirmDeleteModal: boolean = false;
  pagoToDelete: Pago | null = null;

  constructor(
    private eventService: EventService,
    private router: Router,
    private elRef: ElementRef,
    private pagosService: PagosService,
    private partesService: PartesService, // Inyectar PartesService
    private route: ActivatedRoute,
    private casosService: CasosService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.id_caso = this.route.snapshot.paramMap.get('id');
    if (this.id_caso) {
      // Asignar el id del expediente
      this.nuevoPago.id_expediente = this.id_caso;

      // Obtener el estado del expediente y las partes desde el caso
      this.casosService.getCaso(this.id_caso).subscribe({
        next: (caso) => {
          this.estadoExpediente = caso.estado;
          const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
          this.disableOptions = estadosCerrados.includes(this.estadoExpediente ?? '');

          // Asignar las partes directamente del caso recibido
          this.partes = caso.partes.map((parte: Parte) => ({
            value: parte.id!,
            label: parte.razon_social || ''
          }));

          // Configuración de opciones según el nivel de acceso del usuario
          this.authService.currentUser().subscribe(user => {
            if (user?.nivel_acceso === 'ADMIN') {
              this.disableAnadirPago = false;
            }
            this.loaded = true;
          });
        },
        error: (error) => {
          console.error('Error obteniendo el estado del expediente:', error);
          this.error = error;
        }
      });

      // Cargar pagos
      this._fetchData();
    }
  }


  isCaseClosed(): boolean {
    const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
    return estadosCerrados.includes(this.estadoExpediente ?? '');
  }



  _fetchData(): void {
    if (this.id_caso) {
      this.pagosService.getPagos(this.id_caso).subscribe((resPagos) => {
        //console.log("resPagos",resPagos)
        this.records = resPagos;
        this.allDatasearch = this.records; // Guardo una copia de todos los datos para la búsqueda
        this.initTableCofig();
        this.loaded = true;
      },
        (error: string) => {
          this.error = error;
          this.loaded = false;
        });
    }
  }

  /*
  _fetchPartes(): void {
    if (this.id_caso) {
      this.partesService.getPartes(this.id_caso).subscribe({
        next: (data) => {
          // Mapea las partes a objetos con las propiedades 'value' y 'label'
          this.partes = data.map(parte => ({
            value: parte.id!,  // Asegúrate de que el id no es undefined
            label: parte.razon_social || '' // Provee una etiqueta, incluso si razon_social es undefined
          }));
          this.loaded = true;
        },
        error: (error) => {
          console.error('Error loading partes:', error);
          this.error = error;
          this.loaded = false;
        }
      });
    }
  }
    */

  onFocus(field: ModelKeys): void {
    if (field === 'importe') {
      this.nuevoPago[field] = this.nuevoPago[field]?.replace(/\./g, '').replace(' €', '');
    }
  }


  onBlur(field: ModelKeys): void {
    if (field === 'importe') {
      const value = parseFloat((this.nuevoPago[field] || '').replace(',', '.'));
      if (!isNaN(value)) {
        this.nuevoPago[field] = this.getFormattedValue(value);
      }
    }
  }


  getFormattedValue(value: number): string {
    if (isNaN(value) || value === 0) {
      return '0,00 €';
    }
    return value.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  removeFormattedValue(value: string): string {
    // Elimina los puntos como separadores de miles
    let result = value.replace(/\./g, '');

    // Reemplaza la coma con un punto para el formato decimal
    result = result.replace(',', '.');

    // Elimina el símbolo de €
    result = result.replace(' €', '');

    return result;
  }

  onSubmit(f: any): void {
    // Crea una variable temporal para almacenar el importe formateado sin modificar el valor en el formulario
    const importeParaEnviar = parseFloat(this.removeFormattedValue(this.nuevoPago.importe || '0'));

    // Verifica que el valor es un número válido antes de continuar
    if (!isNaN(importeParaEnviar)) {
      const importeFinal = importeParaEnviar.toFixed(2); // Mantén dos decimales para el envío
      // Crear un objeto temporal con el valor formateado para enviar
      const pagoParaEnviar = { ...this.nuevoPago, importe: importeFinal };

      // Solo realiza la operación si el formulario es válido
      if (f.valid && this.id_caso) {
        this.pagosService.newPago(pagoParaEnviar).subscribe(
          (newPago) => {
            this.records.push(newPago); // Añadir el nuevo pago a la lista
            f.resetForm(); // Reinicia el formulario
          },
          (error: string) => {
            //console.error('Error al añadir el pago:', error);
          }
        );
      }
    } else {
      //console.error('El importe es inválido.');
    }
  }



  initTableCofig(): void {
    let currentLanguage: string = getCurrentLanguage();
    const currentTranslations = pagoTranslations[currentLanguage].TABLE;
    this.columns = [
      {
        name: 'concepto',
        label: currentTranslations.CONCEPTO,
        formatter: (record: Pago) => this.formatConcepto(record.concepto),
        width: 245,
      },
      {
        name: 'fecha',
        label: currentTranslations.FECHA,
        formatter: (record: Pago) => record.fecha ? formatDateString(record.fecha, currentLanguage) : '',
        width: 245,
      },
      {
        name: 'importe',
        label: currentTranslations.IMPORTE,
        formatter: (record: Pago) => this.getFormattedValue(parseFloat(record.importe || '')),
        sort: false,
        width: 245,
      },
      {
        name: 'tipo_reclamacion',
        label: currentTranslations.TIPO_RECLAMACION,
        formatter: (record: Pago) => record.tipo_reclamacion,
        sort: false,
        width: 245,
      },
      {
        name: 'parte',
        label: currentTranslations.PARTE,
        formatter: (record: Pago) => this.getParteNameById(record.id_parte),
        width: 245,
      },
      {
        name: 'accionesPagos',
        label: currentTranslations.ACTIONS,
        formatter: (record: Pago) => null,
        width: 360,
        sort: false
      }
    ];
  }

  getParteNameById(id_parte: string | undefined): string {
    const parte = this.partes.find(p => p.value === id_parte);
    return parte ? parte.label : '';
  }

  onSort(event: SortEvent): void {
    if (event.direction === '') {
      this.records = this.allDatasearch;
    } else {
      this.records = [...this.records].sort((a, b) => {
        let res: number;
        if (event.column === 'parte') {
          // Ordenar por nombre de parte en lugar de ID
          const nameA = this.getParteNameById(a.id_parte);
          const nameB = this.getParteNameById(b.id_parte);
          res = this.compare(nameA, nameB);
        } else {
          res = this.compare(a[event.column] as string | number, b[event.column] as string | number);
        }
        return event.direction === 'asc' ? res : -res;
      });
    }
  }


  compare(v1: string | number, v2: string | number): any {
    return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
  }

  normalizeText(text: string): string {
    return text.trim().toLocaleLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  matches(row: Pago, term: string): boolean {
    let currentLanguage: string = getCurrentLanguage();
    return this.normalizeText(this.formatConcepto(row.concepto) as string).includes(this.normalizeText(term))
      || this.normalizeText(this.getParteNameById(row.id_parte) as string).includes(this.normalizeText(term))
      || this.normalizeText(row.tipo_reclamacion?.toString() || '').includes(this.normalizeText(term))
      || this.normalizeText(row.fecha ? formatDateString(row.fecha, currentLanguage) : '' || '').includes(this.normalizeText(term))
      || this.normalizeText(row.importe?.toString() || '').includes(this.normalizeText(term));
  }

  searchData(searchTerm: string): void {
    if (searchTerm === '') {
      this.records = this.allDatasearch;
    } else {
      this.records = this.allDatasearch.filter(record => this.matches(record, searchTerm));
    }
  }

  formatConcepto(concepto: string | undefined): string {
    const conceptoMap: { [key: string]: { es: string; en: string } } = {
      'DERECHO_ADMISION': { es: 'DERECHOS DE ADMISIÓN', en: 'ADMISSION FEES' },
      'HONORARIO_DERECHO_GASTO_EXPEDIENTE': { es: 'HONORARIOS, DERECHOS Y GASTOS', en: 'FEES, RIGHTS, AND EXPENSES' },
      'GASTO_ARBITRO': { es: 'GASTOS DE LOS ÁRBITROS', en: 'ARBITRATORS’ EXPENSES' },
      'ALQUILER_SALA': { es: 'ALQUILER DE SALAS', en: 'ROOM RENTALS' }
    };

    const currentLanguage: string = getCurrentLanguage();

    if (concepto) {
      const translation = conceptoMap[concepto];
      if (translation) {
        return currentLanguage === 'en' ? translation.en : translation.es;
      }
      return concepto.replace(/_/g, ' ');
    }

    return '';
  }



  openDeleteModal(pago: Pago) {
    this.pagoToDelete = pago;
    this.showConfirmDeleteModal = true;
  }

  closeDeleteModal() {
    this.showConfirmDeleteModal = false;
    this.pagoToDelete = null;
  }

  confirmDelete() {
    if (this.pagoToDelete?.id) {
      this.pagosService.deletePago(this.pagoToDelete.id).subscribe(() => {
        this._fetchData(); // Actualiza los datos después de borrar
        this.closeDeleteModal();
      });
    }
  }

}

